import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;  
  font-family: 'sans-serif';

  @media (max-width: 768px) {
    margin: 20px auto;
    padding: 25px 20px;
    max-width: 95%;
  }
  
  @media (max-width: 480px) {
    margin: 15px auto;
    padding: 20px 15px;
    max-width: 98%;
  }
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 350px;
  
  @media (max-width: 768px) {
    max-width: 280px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    max-width: 220px;
    margin-bottom: 18px;
  }
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 32px;
  font-family: 'sans-serif';
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 20px;
  }
`;

const FormImage = styled.img`
  display: block;
  margin: 0 auto 32px auto;
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 32px;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 15px;
    margin-top: 25px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-top: 20px;
  }
`;

const SigLabel = styled.span`
  color: #222;
  
  @media (max-width: 768px) {
    font-weight: 600;
    min-width: 120px;
  }
`;

const SigValue = styled.span`
  min-width: 180px;
  border-bottom: 1px solid #333;
  margin: 0;
  padding: 0 4px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    min-width: 150px;
    margin: 0;
    margin-top: 5px;
  }
  
  @media (max-width: 480px) {
    min-width: 120px;
  }
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 25px;
    font-size: 1rem;
    margin-top: 25px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.95rem;
    margin-top: 20px;
    width: 100%;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Fix signature row spacing for print */
    div[class*="SignatureRow"] {
      display: flex !important;
      align-items: center !important;
      margin-top: 32px !important;
      font-size: 1rem !important;
    }
    
    span[class*="SigLabel"] {
      color: #222 !important;
      margin-left: 0 !important;
    }
    
    span[class*="SigValue"] {
      min-width: 180px !important;
      border-bottom: 1px solid #333 !important;
      margin: 0 !important;
      margin-left: 8px !important;
      padding: 0 4px !important;
      font-weight: 500 !important;
    }
    
    /* Target specific labels for proper spacing */
    div[class*="SignatureRow"] span[class*="SigLabel"]:nth-of-type(3) {
      margin-left: 16px !important;
    }
    
    div[class*="SignatureRow"] span[class*="SigLabel"]:nth-of-type(5) {
      margin-left: 16px !important;
    }
    
    /* Alternative approach using attribute selectors */
    [data-testid="signature-row"] {
      display: flex !important;
      align-items: center !important;
      margin-top: 32px !important;
      font-size: 1rem !important;
    }
    
    [data-testid="sig-label"] {
      color: #222 !important;
      margin-left: 0 !important;
    }
    
    [data-testid="sig-value"] {
      min-width: 180px !important;
      border-bottom: 1px solid #333 !important;
      margin: 0 !important;
      margin-left: 8px !important;
      padding: 0 4px !important;
      font-weight: 500 !important;
    }
  }
`;

const UniformSizeForm = () => {
  const { email } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  return (
    <Container ref={docRef}>
      <PrintHideStyle />
      <Logo src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" />
      
      {/* Mobile responsive styles for text content */}
      <style jsx>{`
        @media (max-width: 768px) {
          .campus-info {
            font-size: 0.9rem !important;
            line-height: 1.5 !important;
          }
          .center-title {
            font-size: 14px !important;
          }
          .website-link {
            font-size: 0.9rem !important;
          }
        }
        @media (max-width: 480px) {
          .campus-info {
            font-size: 0.85rem !important;
            line-height: 1.6 !important;
          }
          .center-title {
            font-size: 0.95rem !important;
          }
          .website-link {
            font-size: 0.85rem !important;
          }
        }
      `}</style>
      
      <div className="center-title" style={{textAlign: 'center', marginBottom: 8, fontWeight: 600, fontSize: '1.1rem'}}>Chicago Community Learning Center</div>
      <div className="campus-info" style={{
        textAlign: 'center', 
        fontSize: '0.98rem', 
        marginBottom: 2,
        lineHeight: '1.4'
      }}>
        Chicago Campus: 840 W. Irving Park Road, suite 203, Illinois 60613 Office: (773) 506-1503<br/>
        Evergreen Campus: 9730 S. Western Ave, suite 502, Evergreen Park Illinois 60805
      </div>
      <div className="website-link" style={{
        textAlign: 'center', 
        fontSize: '0.98rem', 
        marginBottom: 12,
        wordBreak: 'break-word'
      }}>
        <a href="https://www.cclctraining.org" target="_blank" rel="noopener noreferrer">www.cclctraining.org</a>
      </div>
      
      <Title>Unisex Scrub Set Size Chart:</Title>
      
      {/* Display selected uniform size */}
      {student.uniform_size && (
        <div style={{
          border: '1px solid #334293',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <div style={{fontSize: '1.1rem', fontWeight: '600', color: '#334293', marginBottom: '8px'}}>
            Selected Uniform Size
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#2c3e50',
            background: '#fff',
            padding: '8px 16px',
            borderRadius: '6px',
            display: 'inline-block',
            border: '1px solid #334293'
          }}>
            {student.uniform_size}
          </div>
        </div>
      )}
      
      <div style={{overflowX: 'auto', marginBottom: 30}}>
        <table style={{
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '0.98rem', 
          marginBottom: 8,
          display: 'inline-table',
          fontFamily: 'sans-serif',
          minWidth: '600px' // Ensure table doesn't get too cramped on mobile
        }} border="1">
          <thead>
            <tr style={{background: '#f0f0f0'}}>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>Size</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>XXS</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>XSM</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>SML</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>MED</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>LGE</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>XLG</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>2XL</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>3XL</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>4XL</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>5XL</th>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>6XL</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>Chest</th>
              <td style={{
                ...(student.uniform_size === 'XXS' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>28-30</td>
              <td style={{
                ...(student.uniform_size === 'XSM' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>31-33</td>
              <td style={{
                ...(student.uniform_size === 'SML' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>34-36</td>
              <td style={{
                ...(student.uniform_size === 'MED' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>37-39</td>
              <td style={{
                ...(student.uniform_size === 'LGE' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>40-42</td>
              <td style={{
                ...(student.uniform_size === 'XLG' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>43-45</td>
              <td style={{
                ...(student.uniform_size === '2XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>46-48</td>
              <td style={{
                ...(student.uniform_size === '3XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>49-51</td>
              <td style={{
                ...(student.uniform_size === '4XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>52-54</td>
              <td style={{
                ...(student.uniform_size === '5XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>55-57</td>
              <td style={{
                ...(student.uniform_size === '6XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>58-60</td>
            </tr>
            <tr>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>Waist</th>
              <td style={{
                ...(student.uniform_size === 'XXS' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>28-30</td>
              <td style={{
                ...(student.uniform_size === 'XSM' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>31-33</td>
              <td style={{
                ...(student.uniform_size === 'SML' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>34-36</td>
              <td style={{
                ...(student.uniform_size === 'MED' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>37-39</td>
              <td style={{
                ...(student.uniform_size === 'LGE' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>40-42</td>
              <td style={{
                ...(student.uniform_size === 'XLG' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>43-45</td>
              <td style={{
                ...(student.uniform_size === '2XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>46-48</td>
              <td style={{
                ...(student.uniform_size === '3XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>49-51</td>
              <td style={{
                ...(student.uniform_size === '4XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>52-54</td>
              <td style={{
                ...(student.uniform_size === '5XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>55-57</td>
              <td style={{
                ...(student.uniform_size === '6XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>58-60</td>
            </tr>
            <tr>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>Hip</th>
              <td style={{
                ...(student.uniform_size === 'XXS' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>28-30</td>
              <td style={{
                ...(student.uniform_size === 'XSM' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>31-33</td>
              <td style={{
                ...(student.uniform_size === 'SML' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>34-36</td>
              <td style={{
                ...(student.uniform_size === 'MED' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>37-39</td>
              <td style={{
                ...(student.uniform_size === 'LGE' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>40-42</td>
              <td style={{
                ...(student.uniform_size === 'XLG' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>43-45</td>
              <td style={{
                ...(student.uniform_size === '2XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>46-48</td>
              <td style={{
                ...(student.uniform_size === '3XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>49-51</td>
              <td style={{
                ...(student.uniform_size === '4XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>52-54</td>
              <td style={{
                ...(student.uniform_size === '5XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>55-57</td>
              <td style={{
                ...(student.uniform_size === '6XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>58-60</td>
            </tr>
            <tr>
              <th style={{padding: '8px 4px', fontSize: '0.9rem'}}>Center Back Length</th>
              <td style={{
                ...(student.uniform_size === 'XXS' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>25</td>
              <td style={{
                ...(student.uniform_size === 'XSM' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>25.5</td>
              <td style={{
                ...(student.uniform_size === 'SML' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>26.5</td>
              <td style={{
                ...(student.uniform_size === 'MED' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>27.5</td>
              <td style={{
                ...(student.uniform_size === 'LGE' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>28.5</td>
              <td style={{
                ...(student.uniform_size === 'XLG' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>29.5</td>
              <td style={{
                ...(student.uniform_size === '2XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>30.5</td>
              <td style={{
                ...(student.uniform_size === '3XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>31.5</td>
              <td style={{
                ...(student.uniform_size === '4XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>32.5</td>
              <td style={{
                ...(student.uniform_size === '5XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>33.5</td>
              <td style={{
                ...(student.uniform_size === '6XL' ? {background: 'rgb(177, 187, 241)', fontWeight: 'bold'} : {}),
                padding: '8px 4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}>34.5</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Mobile responsive styles for text content */}
      <style jsx>{`
        @media (max-width: 768px) {
          .measurement-instructions {
            font-size: 0.9rem !important;
            line-height: 1.5 !important;
          }
          .size-tip {
            font-size: 0.9rem !important;
            line-height: 1.5 !important;
          }
          .uniform-note {
            font-size: 1rem !important;
            margin: 40px 0 !important;
          }
        }
        @media (max-width: 480px) {
          .measurement-instructions {
            font-size: 0.85rem !important;
            line-height: 1.6 !important;
          }
          .size-tip {
            font-size: 0.85rem !important;
            line-height: 1.6 !important;
          }
          .uniform-note {
            font-size: 0.95rem !important;
            margin: 30px 0 !important;
          }
        }
      `}</style>
      
      <div className="measurement-instructions" style={{fontSize: '0.98rem', marginBottom: 8, fontFamily: 'sans-serif'}}>
        <p><b>Chest</b> - Measures the fullest part of your chest.</p>
        <p><b>Waist</b> - Bend to one side to the natural crease of your waist and measure across this point.</p>
        <p><b>Hip</b> - Standing with feet together, measure around the fullest part of your hip and rear.</p>
        <p><b>Inseam</b> - Measure from the bottom of the crotch seam to the bottom of the hem along the inside seam.</p>
      </div>
      <div className="size-tip" style={{fontSize: '0.98rem', marginBottom: 8}}>
        <b>SIZE TIP:</b> If you're a guy ordering unisex fits scrubs, they should ideally be true to size. For the ladies, consider ordering one Size smaller than you would in a women's fit scrub top or pants
      </div>
      <div className="uniform-note" style={{fontWeight: 700, fontSize: '14px', marginBottom: 12, margin: "60px 0"}}>
        Top and Pants must be same size. Uniform comes in a set and cannot be separated.
      </div>
      
      <div 
        data-testid="signature-row" 
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: '32px',
          fontSize: '1rem',
          gap: '8px'
        }}
      >
        <span 
          data-testid="sig-label" 
          style={{
            color: '#222',
            marginLeft: '0',
            whiteSpace: 'nowrap'
          }}
        >
          Signature:
        </span>
        <span 
          data-testid="sig-value" 
          style={{
            minWidth: '100px',
            borderBottom: '1px solid #333',
            margin: '0',
            marginLeft: '8px',
            padding: '0 4px',
            fontWeight: '500',
            flex: '1'
          }}
        >
          {(() => {
            const sigData = student.student_ack_signature_data;
            
            // Check if it's a valid data URL (must have content after data:)
            if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
              return (
                <img 
                  src={sigData} 
                  alt="Student Signature" 
                  style={{ maxHeight: 40 }} 
                />
              );
            }
            
            // Check if it's SVG markup
            if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
              return (
                <div 
                  dangerouslySetInnerHTML={{ __html: sigData }}
                  style={{ maxHeight: 40 }}
                />
              );
            }
            
            // Check if it's a base64 string (without data URL prefix)
            if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
              return (
                <img 
                  src={`data:image/png;base64,${sigData}`} 
                  alt="Student Signature" 
                  style={{ maxHeight: 40 }} 
                />
              );
            }
            
            // Check if it's a file path
            if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
              return (
                <img 
                  src={sigData} 
                  alt="Student Signature" 
                  style={{ maxHeight: 40 }} 
                />
              );
            }
            
            // Return empty for incomplete or invalid data
            return null;
          })()}
        </span>
        <span 
          data-testid="sig-label" 
          style={{
            color: '#222',
            marginLeft: '16px',
            whiteSpace: 'nowrap'
          }}
        >
          Printed Name:
        </span>
        <span 
          data-testid="sig-value" 
          style={{
            minWidth: '100px',
            borderBottom: '1px solid #333',
            margin: '0',
            marginLeft: '8px',
            padding: '0 4px',
            fontWeight: '500',
            flex: '1'
          }}
        >
          {student.firstName} {student.lastName}
        </span>
        <span 
          data-testid="sig-label" 
          style={{
            color: '#222',
            marginLeft: '16px',
            whiteSpace: 'nowrap'
          }}
        >
          Date:
        </span>
        <span 
          data-testid="sig-value" 
          style={{
            minWidth: '100px',
            borderBottom: '1px solid #333',
            margin: '0',
            marginLeft: '8px',
            padding: '0 4px',
            fontWeight: '500',
            flex: '1'
          }}
        >
          {student.acknowledgementDate || dateStr}
        </span>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default UniformSizeForm; 